import axios from 'axios';
import { getApiProxyUrl } from '@/core/configs/auth/auth.config';
import { ConfiguracoesSistemaDto, obterConfiguracoesSistema } from './configuracoes-sistema.service';

export interface MaintenanceSnapshot {
    isMaintenanceMode: boolean;
    systemName: string;
    description: string;
    startDate: string | null;
    endDate: string | null;
    source: 'api' | 'middleware' | 'fallback';
}

export interface ScheduleMaintenancePayload {
    manutencao: boolean;
    manutencaoDataInicio: string;
    manutencaoDataFim: string;
    manutencaoDescricao: string;
}

export interface ScheduleMaintenanceResult {
    success: boolean;
    data?: boolean | null;
    errors?: string[];
    message?: string;
}

interface ApiResponse<T> {
    success?: boolean;
    data?: T;
    Data?: T;
    errors?: string[];
}

const defaultSystemName = 'TemplateCotinReact';
const maintenanceStorageKey = 'workspace.maintenance.status';
const maintenancePath = '/manutencao';

const defaultSnapshot: MaintenanceSnapshot = {
    isMaintenanceMode: false,
    systemName: defaultSystemName,
    description: '',
    startDate: null,
    endDate: null,
    source: 'fallback',
};

function buildMaintenanceUrl(): string {
    const baseUrl = getApiProxyUrl().replace(/\/$/, '');
    return `${baseUrl}/v1/api/BFFProxy/configuracoesdosistema`;
}

function parseDate(value?: string | null): Date | null {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function isWithinWindow(startDate: string | null, endDate: string | null): boolean {
    const now = new Date();
    const start = parseDate(startDate);
    const end = parseDate(endDate);

    if (!start || !end) {
        return false;
    }

    return now >= start && now <= end;
}

function mapConfigToSnapshot(config?: ConfiguracoesSistemaDto | null): MaintenanceSnapshot {
    if (!config) {
        return { ...defaultSnapshot };
    }

    const startDate = config.manutencaoDataInicio ?? null;
    const endDate = config.manutencaoDataFim ?? null;
    const isFlagEnabled = config.manutencao ?? false;

    if (!isFlagEnabled) {
        return {
            isMaintenanceMode: false,
            systemName: config.nome ?? defaultSystemName,
            description:
                config.manutencaoDescricao ??
                config.descricao ??
                'O sistema está temporariamente indisponível para manutenção.',
            startDate,
            endDate,
            source: 'api',
        };
    }

    return {
        isMaintenanceMode: isFlagEnabled && isWithinWindow(startDate, endDate),
        systemName: config.nome ?? defaultSystemName,
        description:
            config.manutencaoDescricao ??
            config.descricao ??
            'O sistema está temporariamente indisponível para manutenção.',
        startDate,
        endDate,
        source: 'api',
    };
}

function saveSnapshot(snapshot: MaintenanceSnapshot): void {
    try {
        if (!snapshot.isMaintenanceMode) {
            sessionStorage.removeItem(maintenanceStorageKey);
            return;
        }
        sessionStorage.setItem(maintenanceStorageKey, JSON.stringify(snapshot));
    } catch {
        /* ignore */
    }
}

export function getStoredMaintenanceSnapshot(): MaintenanceSnapshot | null {
    try {
        const raw = sessionStorage.getItem(maintenanceStorageKey);
        if (!raw) {
            return null;
        }
        return JSON.parse(raw) as MaintenanceSnapshot;
    } catch {
        return null;
    }
}

export function getMaintenancePath(): string {
    return maintenancePath;
}

export function shouldSkipMaintenanceCheck(path?: string): boolean {
    if (!path) {
        return false;
    }

    return (
        path.includes('/auth/login') ||
        path.includes('/Autenticar/Login') ||
        path.includes('/Autenticar/LoginEmpresa') ||
        path.includes('/Autenticar/UserSession') ||
        path.toLowerCase().includes('/bffproxy/configuracoesdosistema') ||
        path.includes('/maintenance') ||
        path.includes('/manutencao') ||
        path.includes('/circuit-breaker-error')
    );
}

export async function checkMaintenanceStatus(): Promise<MaintenanceSnapshot> {
    try {
        const config = await obterConfiguracoesSistema();
        const snapshot = mapConfigToSnapshot(config);
        saveSnapshot(snapshot);
        return snapshot;
    } catch (error: unknown) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;

        if (status === 400 || status === 404) {
            saveSnapshot(defaultSnapshot);
            return defaultSnapshot;
        }

        const stored = getStoredMaintenanceSnapshot();
        if (!stored) {
            return defaultSnapshot;
        }

        const endDate = parseDate(stored.endDate);
        if (endDate && new Date() > endDate) {
            saveSnapshot(defaultSnapshot);
            return defaultSnapshot;
        }

        return stored;
    }
}

export function saveSnapshotFromMaintenanceError(error: unknown): MaintenanceSnapshot | null {
    if (!axios.isAxiosError(error) || error.response?.status !== 503) {
        return null;
    }

    const stored = getStoredMaintenanceSnapshot();
    const errorBody = error.response.data as {
        details?: string;
        sistemaNome?: string;
    };
    const systemNameFromHeader = error.response.headers?.['x-system-name'] as string | undefined;

    const snapshot: MaintenanceSnapshot = {
        isMaintenanceMode: true,
        systemName: errorBody?.sistemaNome ?? systemNameFromHeader ?? stored?.systemName ?? defaultSystemName,
        description:
            errorBody?.details ??
            stored?.description ??
            'O sistema está temporariamente indisponível para manutenção.',
        startDate: stored?.startDate ?? null,
        endDate: stored?.endDate ?? null,
        source: 'middleware',
    };

    saveSnapshot(snapshot);
    return snapshot;
}

export async function scheduleMaintenance(
    payload: ScheduleMaintenancePayload
): Promise<ScheduleMaintenanceResult> {
    try {
        const response = await axios.patch<ApiResponse<boolean>>(
            `${buildMaintenanceUrl()}/Manutencao`,
            payload,
            { withCredentials: true }
        );

        if (response.data?.success && response.data.data != null) {
            return {
                success: true,
                data: response.data.data,
                errors: response.data.errors,
                message: (response.data as ApiResponse<boolean> & { message?: string }).message,
            };
        }

        return {
            success: false,
            data: false,
            errors: response.data?.errors ?? ['Falha ao agendar manutenção.'],
            message: (response.data as ApiResponse<boolean> & { message?: string })?.message,
        };
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            const body = error.response?.data as { errors?: string[]; message?: string } | undefined;
            const backendErrors = body?.errors;
            return {
                success: false,
                data: false,
                errors: Array.isArray(backendErrors) ? backendErrors : ['Falha ao agendar manutenção.'],
                message: body?.message,
            };
        }

        return {
            success: false,
            data: false,
            errors: ['Falha ao agendar manutenção.'],
        };
    }
}

export function redirectToMaintenanceIfNeeded(snapshot: MaintenanceSnapshot): void {
    if (!snapshot.isMaintenanceMode) {
        return;
    }

    if (window.location.pathname === maintenancePath || window.location.pathname === '/maintenance') {
        return;
    }

    window.location.href = maintenancePath;
}
